import { Button } from '@heroui/react';
import { Trash2 } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DataTable, ModalConfirmation, PageTitle, PageWrapper } from '../../../../components';
import { useHeroToast, useWarmup } from '../../../../hooks';
import { useDeleteDevices, useGetDevicesFromFirestore } from '../../../../queries';
import { DeviceData } from '../../../../types';
import { getDeviceColumns } from './columns';

const PAGE_SIZE = 20;

export const Devices: FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { showSuccess, showError } = useHeroToast();

  useWarmup('v3-deleteDevices');

  const { data, error, isLoading } = useGetDevicesFromFirestore(currentPage, PAGE_SIZE);
  const devices = useMemo(() => data?.data ?? [], [data]);

  const { mutate: deleteDevices, isPending: isDeleting } = useDeleteDevices();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Urządzenia wskazane do usunięcia: single = 1 element, bulk = zaznaczone.
  const [devicesToDelete, setDevicesToDelete] = useState<DeviceData[]>([]);

  // Zaznaczenie dotyczy widocznej strony — po jej zmianie czyścimy je, żeby bulk delete
  // nie skasował urządzeń, których użytkownik już nie widzi.
  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage]);

  const selectedDevices = useMemo(
    () => devices.filter((device) => selectedIds.includes(device.id)),
    [devices, selectedIds],
  );

  const areAllSelected = devices.length > 0 && selectedIds.length === devices.length;
  const isSomeSelected = selectedIds.length > 0;

  const handleToggleRow = (id: string): void => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const handleToggleAll = (): void => {
    setSelectedIds((prev) => (
      prev.length === devices.length ? [] : devices.map((device) => device.id)
    ));
  };

  // Single i bulk lecą tym samym endpointem — single to tablica jednoelementowa.
  const handleDelete = (): void => {
    const ids = devicesToDelete.map((device) => device.id);
    setDevicesToDelete([]);

    deleteDevices(
      { ids },
      {
        onSuccess: (result) => {
          setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));

          if (result.failedIds.length > 0) {
            showError(`Nie udało się usunąć ${result.failedIds.length} z ${ids.length} urządzeń`);
            return;
          }

          showSuccess(
            result.deletedCount === 1
              ? 'Device deleted successfully'
              : `Usunięto ${result.deletedCount} urządzeń`,
          );
        },
        onError: (err) => {
          const { message } = (err.response?.data ?? {}) as { message?: string };
          showError(message || 'Device deletion failed');
        },
      },
    );
  };

  const columns = useMemo(
    () => getDeviceColumns({
      selectedIds,
      onToggleRow: handleToggleRow,
      onToggleAll: handleToggleAll,
      areAllSelected,
      isSomeSelected,
      onDelete: (device) => setDevicesToDelete([device]),
      isDeleting,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, devices, areAllSelected, isSomeSelected, isDeleting],
  );

  const isBulk = devicesToDelete.length > 1;

  return (
    <PageWrapper>
      <PageTitle
        title="Devices"
        buttonText="Add new"
        buttonOnClick={() => navigate('/devices/new')}
        actions={(
          <Button
            size="sm"
            color="danger"
            variant="flat"
            radius="sm"
            startContent={<Trash2 size={14} />}
            isDisabled={selectedDevices.length === 0 || isDeleting}
            isLoading={isDeleting}
            onPress={() => setDevicesToDelete(selectedDevices)}
          >
            {`Usuń zaznaczone (${selectedDevices.length})`}
          </Button>
        )}
      />

      {/* Single: potwierdzenie wpisaniem nazwy (jak dotąd). Bulk: potwierdzenie z liczbą. */}
      <ModalConfirmation
        needConfirm={!isBulk}
        confirmValue={devicesToDelete[0]?.name ?? devicesToDelete[0]?.id ?? ''}
        isOpen={devicesToDelete.length > 0}
        onClose={() => setDevicesToDelete([])}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        title={isBulk ? `Delete ${devicesToDelete.length} devices` : 'Delete device'}
        description={isBulk
          ? `Czy na pewno chcesz usunąć ${devicesToDelete.length} zaznaczonych urządzeń? Operacja jest nieodwracalna.`
          : 'Are you sure you want to delete this device? This action will permanently remove the device and cannot be undone.'}
      />

      <DataTable
        data={devices}
        columns={columns}
        isLoading={isLoading}
        isError={!!error}
        pageSize={PAGE_SIZE}
        totalPages={data?.totalPages ?? 1}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        searchPlaceholder="Search by name, serial..."
      />
    </PageWrapper>
  );
};
